import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import ClaimLegalToDriveModal from './ClaimLegalToDriveModal'
import { PrimaryButton, ChevronRightIcon } from './ui'
import { useApi } from '../services/api'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import { getPolicyType, type ClaimablePolicyType } from '../utils/policyType'
import { readPolicyFromListCache } from '../utils/policiesListCache'
import { CLAIM_FLOW_PATH } from '../claims/claimFlowModel'
import { markLegalToDriveConfirmed } from '../claims/claimLegalToDriveGate'
type PreClaimLocationState = { policy?: PolicyLink; preserveScroll?: boolean } | null

interface ChecklistItem {
  title: string
  /** Omit for a single regular-weight line (design may show bold; portal does not). */
  body?: string
}

interface ExpectStep {
  title: string
  body: string
}

const BEFORE_YOU_START: ChecklistItem[] = [
  {
    title: 'Make sure you are safe',
    body: 'Call 000 in an emergency. For urgent towing and/or assistance, call us on 1300 296 296.',
  },
  {
    title: 'If needed, report the incident to police',
    body: 'Rules vary depending on which state or territory you are in',
  },
]

const WHAT_TO_EXPECT: ExpectStep[] = [
  {
    title: 'Choose your claim type',
    body: 'Pick windscreen or glass, or another type of claim, and tell us when and where the incident happened.',
  },
  {
    title: 'Answer a few questions',
    body: "We'll ask for details relevant to your situation. This might include damage, drivers, repair options, or business and tax details, depending on your claim. Please give us complete and accurate information wherever possible.",
  },
  {
    title: 'Review and submit',
    body: "Check what you've told us, your contact details, and policy information, then lodge your claim online.",
  },
  {
    title: 'What happens next',
    body: "You'll receive a claim number and our team will contact you to finalise your lodgement and confirm next steps.",
  },
]

function ChecklistRow({ item }: { item: ChecklistItem }) {
  return (
    <div className="flex gap-[12px] items-start relative shrink-0 w-full">
      <img
        src="/tick.svg"
        alt=""
        aria-hidden="true"
        className="shrink-0 size-[24px] mt-[2px]"
      />
      <div className="flex flex-col gap-[4px] items-start min-w-0 flex-1">
        {item.body ? (
          <>
            <p className="font-body font-semibold text-md leading-md text-text-primary-900">
              {item.title}
            </p>
            <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
              {item.body}
            </p>
          </>
        ) : (
          <p className="font-body font-regular text-md leading-md text-text-primary-900">
            {item.title}
          </p>
        )}
      </div>
    </div>
  )
}

function StepRow({ step, index, isLast }: { step: ExpectStep; index: number; isLast: boolean }) {
  return (
    <div className="flex gap-[12px] items-start relative shrink-0 w-full">
      <div className="flex flex-col items-center shrink-0 self-stretch">
        <div className="bg-background-primary border border-border-primary border-solid flex items-center justify-center rounded-full size-[28px] shrink-0">
          <span className="font-body font-medium text-sm leading-sm text-text-secondary-700">
            {index + 1}
          </span>
        </div>
        {!isLast && (
          <div
            aria-hidden="true"
            className="flex-1 w-px mt-[4px] mb-[-12px] border-l border-dashed border-border-secondary"
          />
        )}
      </div>
      <div className={`flex flex-col gap-[4px] items-start min-w-0 flex-1 ${isLast ? '' : 'pb-[20px]'}`}>
        <p className="font-body font-semibold text-md leading-md text-text-primary-900">
          {step.title}
        </p>
        <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
          {step.body}
        </p>
      </div>
    </div>
  )
}

export default function PreClaim() {
  const navigate = useNavigate()
  const location = useLocation()
  const { getPolicies } = useApi()
  const policyRefFromQuery = decodePolicyRef(new URLSearchParams(location.search).get('policyRef'))
  const navState = (location.state as PreClaimLocationState) ?? null

  const [policy, setPolicy] = useState<PolicyLink | null>(
    () => navState?.policy ?? readPolicyFromListCache(policyRefFromQuery)
  )
  const [legalToDriveModalOpen, setLegalToDriveModalOpen] = useState(false)

  // Only fetch if we have no policy from navigation state (Claims landing / dashboard) or list cache
  useEffect(() => {
    if (policy) return

    let cancelled = false

    const fetchPolicies = async () => {
      try {
        const response = await getPolicies()
        if (cancelled) return
        if (!response.policies || response.policies.length === 0) {
          setPolicy(null)
          return
        }

        const matching = policyRefFromQuery
          ? response.policies.find((p) => p.policyRef === policyRefFromQuery)
          : response.policies[0]
        setPolicy((matching || response.policies[0]) as PolicyLink)
      } catch (error) {
        console.error('Error fetching policies for pre-claim:', error)
        if (!cancelled) setPolicy(null)
      }
    }

    void fetchPolicies()
    return () => {
      cancelled = true
    }
  }, [getPolicies, policyRefFromQuery, policy])

  const targetPolicyRef = policy?.policyRef || policyRefFromQuery

  const handleBack = () => {
    if (targetPolicyRef) {
      navigate(`/claims?policyRef=${encodeURIComponent(targetPolicyRef)}`, {
        state: policy ? { policy, preserveScroll: true } : { preserveScroll: true },
      })
    } else {
      navigate('/claims')
    }
  }

  const handleStartClaim = () => {
    setLegalToDriveModalOpen(true)
  }

  const navigateToSelectType = () => {
    if (!targetPolicyRef) {
      navigate(CLAIM_FLOW_PATH.selectType)
      return
    }
    const params = new URLSearchParams({ policyRef: targetPolicyRef })
    const resolvedType = policy ? getPolicyType(policy) : null
    if (resolvedType) {
      params.set('type', resolvedType)
    }
    navigate(`${CLAIM_FLOW_PATH.selectType}?${params.toString()}`, {
      state: policy ? { policy } : undefined,
    })
  }

  const handleLegalToDriveConfirm = () => {
    markLegalToDriveConfirmed(targetPolicyRef ?? null)
    setLegalToDriveModalOpen(false)
    navigateToSelectType()
  }

  const handleVehicleNotSafeToDrive = () => {
    setLegalToDriveModalOpen(false)
    const resolvedType = policy ? getPolicyType(policy) : null
    const typeHint: ClaimablePolicyType | null =
      resolvedType === '4x4' || resolvedType === 'caravan' || resolvedType === 'camper'
        ? resolvedType
        : null
    navigate('/claims/call-for-assistance', {
      state: {
        policy: policy ?? undefined,
        policyRef: targetPolicyRef ?? null,
        typeHint,
      },
    })
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back to claims"
        onBack={handleBack}
        eyebrow="Claim"
        title="Make a claim"
      />

      <ClaimsFlowMain>
          <section
            aria-labelledby="before-you-start-heading"
            className="flex flex-col gap-[20px] items-start w-full"
          >
            <h2
              id="before-you-start-heading"
              className="font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
            >
              Before you start
            </h2>
            <div className="flex flex-col gap-[20px] items-start w-full">
              {BEFORE_YOU_START.map((item) => (
                <ChecklistRow key={item.title} item={item} />
              ))}
            </div>
          </section>

          <section
            aria-labelledby="what-to-expect-heading"
            className="flex flex-col gap-[16px] items-start w-full"
          >
            <h2
              id="what-to-expect-heading"
              className="font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
            >
              What to expect
            </h2>
            <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
              Most claims take about{' '}
              <span className="font-semibold text-text-primary-900">5 minutes</span>, depending on the type of
              claim.
            </p>
            <div className="flex flex-col items-start w-full">
              {WHAT_TO_EXPECT.map((step, idx) => (
                <StepRow
                  key={step.title}
                  step={step}
                  index={idx}
                  isLast={idx === WHAT_TO_EXPECT.length - 1}
                />
              ))}
            </div>
          </section>

          <div className="sticky bottom-0 w-full bg-background-secondary pt-[8px] pb-[8px] md:static md:py-0">
            <PrimaryButton
              onClick={handleStartClaim}
              size="lg"
              fullWidth
              rightIcon={
                <div className="size-5 text-text-white">
                  <ChevronRightIcon />
                </div>
              }
              aria-label="Start claim"
            >
              Start claim
            </PrimaryButton>
          </div>
      </ClaimsFlowMain>

      {legalToDriveModalOpen ? (
        <ClaimLegalToDriveModal
          onClose={() => setLegalToDriveModalOpen(false)}
          onConfirm={handleLegalToDriveConfirm}
          onVehicleNotSafe={handleVehicleNotSafeToDrive}
        />
      ) : null}

      <Footer />
    </div>
  )
}
